import React, { useMemo } from 'react';
import type { ProjectPlan } from '../../../../shared/types/plans';
import { PLANS, SELLABLE_PLANS } from '../../../../shared/types/plans';
import type { OnboardingAnswers } from '../../../../shared/types/accounts';
import { recommendPlan } from '../../../../shared/lib/planRecommendation';

interface Props {
  answers: OnboardingAnswers;
  chosenPlan: ProjectPlan;
  onChoose: (plan: ProjectPlan) => void;
}

const PLAN_COLORS: Record<ProjectPlan, { ring: string; badge: string; text: string }> = {
  starter:    { ring: 'border-slate-300',   badge: 'bg-slate-100 text-slate-700',    text: 'text-slate-700' },
  team:       { ring: 'border-blue-300',    badge: 'bg-blue-50 text-blue-700',        text: 'text-blue-700' },
  business:   { ring: 'border-violet-300',  badge: 'bg-violet-50 text-violet-700',    text: 'text-violet-700' },
  enterprise: { ring: 'border-amber-300',   badge: 'bg-amber-50 text-amber-700',      text: 'text-amber-700' },
};

/**
 * UX-08: body-only — the selectable plan-card grid from the old
 * `StepPlan` step (its own Back/Continue/loading footer moved up to
 * `StepCodebase`, since this now lives inside a "Change plan"
 * disclosure rather than being its own wizard step).
 */
export const PlanFields: React.FC<Props> = ({ answers, chosenPlan, onChoose }) => {
  const rec = useMemo(() => recommendPlan(answers), [answers]);

  return (
    <div className="space-y-3">
      {rec.reasons.length > 0 && (
        <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-3">
          <strong>Recommended because:</strong>{' '}
          {rec.reasons.map((r) => r.answer).join(', ')}.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* FORGE-262: starter is not sold — only sellable plans are selectable at signup. */}
        {SELLABLE_PLANS.map((p) => {
          const def = PLANS[p];
          const colors = PLAN_COLORS[p];
          const isRecommended = p === rec.plan;
          const isChosen = p === chosenPlan;
          const fit = rec.perPlanFit[p];
          return (
            <button
              key={p}
              type="button"
              onClick={() => onChoose(p)}
              className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                isChosen ? colors.ring + ' shadow-sm' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {isRecommended && (
                <span className="absolute -top-2.5 left-4 text-xs font-semibold bg-primary-600 text-white px-2 py-0.5 rounded-full">
                  Recommended
                </span>
              )}
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-semibold ${colors.text}`}>{def.label}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>{def.priceLabel}</span>
              </div>
              <p className="text-xs text-slate-500">{def.support}</p>
              {!fit.ok && (
                <p className="mt-2 text-xs text-danger-700 bg-danger-50 rounded px-2 py-1">
                  Requires upgrade: {fit.blockers.slice(0, 2).join(', ')}
                  {fit.blockers.length > 2 ? ` +${fit.blockers.length - 2} more` : ''}
                </p>
              )}
              {p === 'enterprise' && (
                <a
                  href="mailto:sales@forgehestus.com?subject=Forge Enterprise"
                  className="mt-2 block text-xs text-amber-700 underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Talk to sales
                </a>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
